import {
  Component,
  inject,
  signal,
  computed,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TableFilterBarComponent, FilterGroup } from '@custom-components/general/table-filter-bar';
import { FilledButtonComponent, TextButtonComponent } from '@custom-components/general/button';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import {
  APP_PATHS,
  FIRMWARE_MANAGEMENT_TABLE_COLUMNS_CONFIG,
  PAGINATION_DEFAULT,
} from '@core/constants';
import { DeviceModel, FirmwareModel } from '@core/models';
import { TagStateOf } from '@core/helpers';
import {
  bulkUpdateLimitFor,
  FIRMWARE_STATUS_I18N_KEYS,
  FIRMWARE_STATUSES,
  FirmwareStatus,
  resolveFirmwareCatalogueState,
  resolveFirmwareTransport,
} from '@core/utils/firmware-catalogue.util';
import { DeviceStore } from '@root/app/store/device.store';
import {
  FirmwareActions,
  selectFirmwareItems,
  selectFirmwareUpdateSuccess,
} from '@root/app/store/firmware.index';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BulkFirmwareUpdateDialogComponent } from '../../components/bulk-firmware-update-dialog/bulk-firmware-update-dialog.component';
import { FirmwareUpdateDialogComponent } from '../../components/firmware-update-dialog/firmware-update-dialog.component';

@Component({
  selector: 'xemex-firmware-management-page',
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    RouterModule,
    TableComponent,
    TableFilterBarComponent,
    PageHeaderComponent,
    PageBodyComponent,
    TranslateModule,
    FilledButtonComponent,
    TextButtonComponent,
  ],
  providers: [DeviceStore],
  templateUrl: './firmware-management-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './firmware-management-page.component.scss',
})
export class FirmwareManagementPageComponent {
  readonly deviceStore = inject(DeviceStore);
  readonly store = inject(Store);
  readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly APP_PATHS = APP_PATHS;
  readonly columns = FIRMWARE_MANAGEMENT_TABLE_COLUMNS_CONFIG;

  protected readonly availableFirmwares = signal<FirmwareModel[]>([]);
  protected readonly selectedDevices = signal<DeviceModel[]>([]);
  protected readonly firmwareVersionFilter = signal('');
  protected readonly installerFilter = signal('');
  protected readonly updateStatusFilter = signal<FirmwareStatus | ''>('');

  /** Translated status label → canonical status, so the filter bar can show localized options
   * while the rows keep comparing on a stable key. */
  private readonly statusLabelToKey = new Map<string, FirmwareStatus>(
    FIRMWARE_STATUSES.map(status => [this.translateStatus(status), status]),
  );

  protected readonly filterGroups = computed<FilterGroup[]>(() => {
    const devices = this.deviceStore.deviceData().devices;
    return [
      {
        key: 'firmwareVersion',
        label: this.translate.instant('firmwareManagement.table.firmwareVersion'),
        options: [
          ...new Set(
            devices.filter(d => d.deviceFirmwareVersion).map(d => d.deviceFirmwareVersion),
          ),
        ],
      },
      {
        key: 'installer',
        label: this.translate.instant('firmwareManagement.table.installer'),
        options: [...new Set(devices.filter(d => d.customerName).map(d => d.customerName))],
      },
      {
        key: 'updateStatus',
        label: this.translate.instant('firmwareManagement.filter.updateStatus'),
        options: FIRMWARE_STATUSES.map(status => this.translateStatus(status)),
      },
    ];
  });

  protected readonly tableData = computed(() => {
    const fwVersion = this.firmwareVersionFilter();
    const installer = this.installerFilter();
    const updateStatus = this.updateStatusFilter();

    return this.deviceStore
      .deviceData()
      .devices.filter(d => !fwVersion || d.deviceFirmwareVersion === fwVersion)
      .filter(d => !installer || d.customerName === installer)
      .filter(d => !updateStatus || this.getFirmwareStatus(d) === updateStatus)
      .map(d => {
        const firmwareStatusKey = this.getFirmwareStatus(d);
        return {
          ...d,
          firmwareStatusKey,
          firmwareStatus: this.translateStatus(firmwareStatusKey),
          firmwareStatusState: TagStateOf.firmwareStatus(firmwareStatusKey),
        };
      }) as unknown as Record<string, unknown>[];
  });

  protected readonly totalCount = computed(() => this.deviceStore.deviceData().numberOfItems);

  /** `GET /devices` does not return `protocol`, so the transport of a row can only be resolved
   * through its deviceTypeId against the device-type catalogue. */
  protected readonly deviceTypeProtocols = computed(
    () => this.deviceStore.deviceTypes()?.['device-types'] ?? [],
  );

  get selectedCount(): number {
    return this.selectedDevices().length;
  }

  constructor() {
    this.loadDevices();
    this.loadFirmwareVersions();

    if (!this.deviceStore.deviceTypes()) {
      this.deviceStore.getDeviceTypesMethod();
    }

    this.store
      .select(selectFirmwareItems)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(items => this.availableFirmwares.set(items));

    this.store
      .select(selectFirmwareUpdateSuccess)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(success => {
        if (success === true) this.onRefreshClicked();
      });
  }

  loadDevices(): void {
    this.deviceStore.getFirmwareDevicesMethod(PAGINATION_DEFAULT.offset);
  }

  loadFirmwareVersions(): void {
    this.store.dispatch(FirmwareActions.getFirmwareVersions({ filter: { isActive: true } }));
  }

  protected translateStatus(status: FirmwareStatus): string {
    return this.translate.instant(FIRMWARE_STATUS_I18N_KEYS[status]);
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    this.firmwareVersionFilter.set(filters['firmwareVersion']?.[0] ?? '');
    this.installerFilter.set(filters['installer']?.[0] ?? '');
    const statusLabel = filters['updateStatus']?.[0];
    this.updateStatusFilter.set(statusLabel ? (this.statusLabelToKey.get(statusLabel) ?? '') : '');
  }

  protected onSelectedRowsChange(rows: Record<string, unknown>[]): void {
    this.selectedDevices.set(rows as unknown as DeviceModel[]);
  }

  protected onRowClick(row: Record<string, unknown>): void {
    const device = row as unknown as DeviceModel;
    this.openUpdateDialog(device);
  }

  protected onPageChange(evt: PageEvent): void {
    this.deviceStore.getFirmwareDevicesMethod(evt.pageIndex);
  }

  protected onRefreshClicked(): void {
    this.deviceStore.refreshFirmwareDevicesMethod();
    this.loadFirmwareVersions();
  }

  protected onBulkUpdateClicked(): void {
    const selected = this.selectedDevices();
    if (!selected.length) return;

    const transport = resolveFirmwareTransport(selected[0], this.deviceTypeProtocols());
    const catalogueState = resolveFirmwareCatalogueState(
      this.availableFirmwares(),
      selected[0].deviceTypeId,
      undefined,
    );

    const dialogRef = this.dialog.open(BulkFirmwareUpdateDialogComponent, {
      width: '600px',
      data: {
        selectedDevices: selected,
        transport,
        maxDevices: bulkUpdateLimitFor(transport),
        availableFirmwares: catalogueState.offered,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.store.dispatch(FirmwareActions.resetBulkUpdateState());
        this.selectedDevices.set([]);
      });
  }

  private openUpdateDialog(device: DeviceModel): void {
    const catalogueState = resolveFirmwareCatalogueState(
      this.availableFirmwares(),
      device.deviceTypeId,
      device.deviceFirmwareVersion,
    );
    if (!catalogueState.offered.length) return;

    const dialogRef = this.dialog.open(FirmwareUpdateDialogComponent, {
      width: '500px',
      data: {
        device,
        transport: resolveFirmwareTransport(device, this.deviceTypeProtocols()),
        availableFirmwares: catalogueState.offered,
        installedNotInCatalogue: catalogueState.installedNotInCatalogue,
        currentVersion: device.deviceFirmwareVersion,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.store.dispatch(FirmwareActions.resetUpdateState()));
  }

  private getFirmwareStatus(device: DeviceModel): FirmwareStatus {
    return resolveFirmwareCatalogueState(
      this.availableFirmwares(),
      device.deviceTypeId,
      device.deviceFirmwareVersion,
    ).status;
  }
}
